import pick from "lodash/pick";
import { DetailsPanelData } from "PFApp/booking/components/details_panel";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringify";
import { useBooking } from "PFCore/hooks/queries";
import { useBookingTemplate } from "PFCore/hooks/queries/bookings/booking_templates/use_booking_template";
import { useProfile } from "PFCore/hooks/queries/profile/use_profile";
import { PropsWithChildren, useEffect, useMemo } from "react";

import { useBookingSiblings } from "../booking_detail_content/booking_siblings/use_booking_siblings";
import { BookingDetailSidePanelContext } from "./booking_detail_side_panel_context";
import { BookingDetailSidePanelProfile } from "./booking_detail_side_panel_context.types";

type BookingDetailSidePanelContextProviderProps = {
  data: DetailsPanelData;
  enabled: boolean;
  activeBookingIndex: number | undefined;
  setActiveBookingIndex: (index: number | undefined) => void;
};

export const BookingDetailSidePanelContextProvider = ({
  children,
  data,
  enabled,
  activeBookingIndex,
  setActiveBookingIndex
}: PropsWithChildren<BookingDetailSidePanelContextProviderProps>) => {
  const stringify = useDeterministicStringify();

  const { id: bookingId, suspendedProfileDetails } = data;
  const { data: initialBooking, isLoading: isInitialBookingLoading } = useBooking(bookingId as number, {
    enabled: enabled && !!bookingId
  });

  const { data: profileData, isError: isProfileError } = useProfile(initialBooking?.profile_id, {
    enabled: enabled && !!initialBooking?.profile_id && !suspendedProfileDetails
  });
  const profile: BookingDetailSidePanelProfile | undefined = profileData
    ? {
        ...pick(profileData, ["id", "status"]),
        firstName: profileData.firstName,
        lastName: profileData.lastName ?? undefined,
        suspendedAt: profileData.suspendedAt
      }
    : suspendedProfileDetails;

  const isRepeatedBooking = !!initialBooking?.booking_template_id;
  const {
    data: siblingBookings,
    isFetching: isSiblingBookingsFetching,
    invalidate: invalidateBookingSiblings
  } = useBookingSiblings({
    bookingId: initialBooking?.id,
    profileId: profile?.id,
    activityId: initialBooking?.activity?.id,
    bookingTemplateId: initialBooking?.booking_template_id,
    enabled: !!initialBooking && !!profile
  });
  const { data: bookingTemplate, isFetching: isBookingTemplateFetching } = useBookingTemplate(
    initialBooking?.booking_template_id,
    {
      enabled: isRepeatedBooking
    }
  );

  const siblingBookingsIds = siblingBookings?.map(({ id }) => id) ?? [];

  useEffect(() => {
    const index = (siblingBookings ?? []).findIndex(({ id }) => id === bookingId);
    setActiveBookingIndex?.(index < 0 ? undefined : index);
  }, [bookingId, stringify(siblingBookingsIds)]);

  const value = useMemo(
    () => ({
      initialBooking,
      profile,
      isProfileError,
      siblingBookings,
      bookingTemplate,
      isInitialBookingLoading,
      isSiblingBookingsFetching,
      isBookingTemplateFetching,
      activeBookingIndex,
      setActiveBookingIndex,
      invalidateBookingSiblings
    }),
    [
      initialBooking,
      profile,
      isProfileError,
      siblingBookings,
      bookingTemplate,
      isInitialBookingLoading,
      isSiblingBookingsFetching,
      isBookingTemplateFetching,
      activeBookingIndex,
      setActiveBookingIndex,
      invalidateBookingSiblings
    ]
  );

  return (
    <BookingDetailSidePanelContext.Provider value={value}>{children}</BookingDetailSidePanelContext.Provider>
  );
};
